import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMap } from "react-leaflet";
import L from "leaflet";
import { overflowShift, popupPadding, screenShiftToPan } from "@/lib/mapPopup";

// SKYCORE MAP.NAV.3 — popup drone CONTRÔLÉE (une instance Leaflet par ouverture `seq`). Le contenu React
// est rendu par portail : la télémétrie met à jour le DOM sans détruire la popup. autoPan Leaflet
// désactivé (il ignore la rotation) : maintien dans le cadre VISIBLE à l'ouverture / redimensionnement
// uniquement, jamais en SUIVRE (le recentrage suivi le défairait -> oscillation).
export default function DronePopupLayer({ seq, latlng, limits, following, rotating, bearingRef, outerRef, dragRef, onClose, children }) {
  const map = useMap();
  const popupRef = useRef(null);
  const [node, setNode] = useState(null);
  const ctx = useRef({});
  ctx.current = { latlng, limits, following, rotating, onClose };
  const lat = latlng ? latlng[0] : null, lng = latlng ? latlng[1] : null;

  const keepInView = () => {
    const p = popupRef.current, outer = outerRef.current, c = ctx.current;
    if (!p || !outer || c.following || (dragRef && dragRef.current)) return;
    const el = p.getElement && p.getElement();
    const wrap = el && el.querySelector(".leaflet-popup-content-wrapper");
    if (!wrap) return;
    const [sx, sy] = overflowShift(wrap.getBoundingClientRect(), outer.getBoundingClientRect(), popupPadding(c.rotating));
    if (sx || sy) map.panBy(screenShiftToPan(sx, sy, bearingRef.current), { animate: true, duration: 0.25 });
  };
  const keepRef = useRef(keepInView);
  keepRef.current = keepInView;

  useEffect(() => {
    const c = ctx.current;
    if (!seq || !c.latlng) return undefined;
    const p = L.popup({ autoPan: false, closeOnClick: false, autoClose: true, className: "skycore-drone-popup", offset: [0, -16], ...c.limits });
    const content = document.createElement("div");
    p.setContent(content).setLatLng(c.latlng);
    const onRemove = () => ctx.current.onClose(seq);
    p.on("remove", onRemove);
    p.openOn(map);
    const btn = p.getElement() && p.getElement().querySelector(".leaflet-popup-close-button");
    if (btn) btn.setAttribute("data-testid", "drone-popup-close");
    popupRef.current = p;
    setNode(content);
    let ro = null, roRaf = null, last = "";
    if (typeof ResizeObserver !== "undefined") {
      // Différé d'une frame (jamais de mise en page synchrone dans le callback -> pas de boucle RO).
      ro = new ResizeObserver((entries) => {
        const r = entries[0] && entries[0].contentRect;
        const k = r ? `${Math.round(r.width)}x${Math.round(r.height)}` : "";
        if (k === last || roRaf != null) return;
        last = k;
        roRaf = requestAnimationFrame(() => { roRaf = null; if (popupRef.current === p) { p.update(); keepRef.current(); } });
      });
      ro.observe(content);
    }
    const raf = requestAnimationFrame(() => keepRef.current());
    return () => {
      cancelAnimationFrame(raf);
      if (roRaf != null) cancelAnimationFrame(roRaf);
      if (ro) ro.disconnect();
      p.off("remove", onRemove);
      map.removeLayer(p);
      popupRef.current = null;
      setNode(null);
    };
  }, [map, seq]);

  useEffect(() => {
    const p = popupRef.current;
    if (!p || lat == null || lng == null) return;
    const cur = p.getLatLng();
    if (!cur || cur.lat !== lat || cur.lng !== lng) p.setLatLng([lat, lng]);
  }, [lat, lng]);

  const { maxWidth, minWidth, maxHeight } = limits;
  useEffect(() => {
    const p = popupRef.current;
    if (!p) return;
    Object.assign(p.options, { maxWidth, minWidth, maxHeight });
    p.update();
    keepRef.current();
  }, [maxWidth, minWidth, maxHeight]);

  return node ? createPortal(children, node) : null;
}
